import { useTeamCatalog, withTeamCatalog } from '@context/TeamCatalog.context'
import React, { useEffect, useMemo, useState, ReactNode } from 'react'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import {
  PhoneManagementProvider,
  usePhoneManagementCtx,
} from './PhoneManagement.context'
import './PhoneManagementStyle.css'
import {
  ClinicInternalExtensionRow,
  ITwilioPortRequest,
  ClinicNumberRow,
} from '@dataTypes/clinic.type'
import EditPhoneNumber from './EditPhoneNumber/EditPhoneNumber.component'
import AddExtension from './AddExtension/AddExtension.component'
import { safeParseMethods } from '@utils/phoneManagement.utils'
import AddPhoneNumber from './AddPhoneNumber/AddPhoneNumber.component'
import PortNumberModal from './PortNumber/PortNumberModal.component'
import PortFaxModal from '../Fax/PortNumber/PortFaxModal.component'
import { formatDate } from '@utils/date.utils'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import { useSettingsTabSkeleton } from '@pages/Settings/useSettingsTabSkeleton'
import PurchaseNumberModal from './PurchaseNumber/PurchaseNumberModal.component'
import PhoneManagementDeleteNumberModal from './PhoneManagementDeleteNumberModal.component'
import PhoneManagementDeleteExtensionModal from './PhoneManagementDeleteExtensionModal.component'
import {
  getPortRequestTypeLabel,
  isFaxPortRequest,
} from '@utils/portRequestPayload.util'
import {
  formatPhoneNumber,
  normalizePhoneNumber,
} from '@utils/numberFormatter.util'
import {
  Badge,
  Tabs,
  SearchWithFilter,
  Button,
  EmptyState,
  DataTableColumn,
  DataTable,
  ActionButtons,
  ActionButton,
  type BadgeColor,
  type DataTableSortDirection,
  Select,
} from '@enamelai/design-system'
import { Icon as CloseIcon } from '@enamelai/design-system/icons/Close'
import { Icon as DeleteFilledIcon } from '@enamelai/design-system/icons/Delete2'
import { Icon as EditIcon } from '@enamelai/design-system/icons/Edit'
import { Icon as RefreshIcon } from '@enamelai/design-system/icons/Replace'
import { Icon as PhoneIcon } from '@enamelai/design-system/icons/Call'
import { Icon as PortIcon } from '@enamelai/design-system/icons/Recurring'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'

export function methodPill(key: string): ReactNode {
  const k = key.toLowerCase()
  if (k === 'fax') return <Badge mode="tinted" color="yellow" label="Fax" />
  if (k === 'sms') return <Badge mode="tinted" color="cyan" label="SMS" />
  if (k === 'mms')
    return <Badge mode="tinted" color="pale-green" label="Voicemail" />
  if (k === 'call') return <Badge mode="tinted" color="blue" label="Call" />
  return key
}

type PhoneTableRow = { id: string }

type PhoneSortColumn = 'line_name' | 'phone_number'

type PortSortColumn =
  | 'phoneNumbers'
  | 'portType'
  | 'phone_carrier'
  | 'date_time_created_at'
  | 'status'

type ExtensionSortColumn = 'name' | 'phone_number' | 'team' | 'extension_number'

const DEFAULT_PAGE_SIZE = 10

const textSortValue = (value?: string | null): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed.toLowerCase() : null
}

const compareSortValues = (
  aValue: string | number | null,
  bValue: string | number | null,
  direction: DataTableSortDirection,
): number => {
  const multiplier = direction === 'asc' ? 1 : -1
  if (aValue === bValue) return 0
  if (aValue === null) return 1
  if (bValue === null) return -1
  return aValue < bValue ? -multiplier : multiplier
}

const sortRows = <T,>(
  rows: T[],
  active: boolean,
  direction: DataTableSortDirection,
  getValue: (row: T) => string | number | null,
): T[] => {
  if (!active) return rows
  return [...rows].sort((left, right) =>
    compareSortValues(getValue(left), getValue(right), direction),
  )
}

const getPhoneSortValue = (
  row: ClinicNumberRow,
  column: PhoneSortColumn,
): string | number | null => {
  switch (column) {
    case 'line_name':
      return textSortValue(row.line_name)
    case 'phone_number':
      return textSortValue(normalizePhoneNumber(row.phone_number))
  }
}

const formatPortPhoneNumbers = (request: ITwilioPortRequest): string =>
  [...request.phone_numbers]
    .filter((phoneNumber): phoneNumber is string => phoneNumber != null)
    .map((phoneNumber) => normalizePhoneNumber(phoneNumber))
    .sort()
    .map((phoneNumber) => formatPhoneNumber(phoneNumber))
    .join(', ')

const getPortSortValue = (
  request: ITwilioPortRequest,
  column: PortSortColumn,
): string | number | null => {
  switch (column) {
    case 'phoneNumbers':
      return textSortValue(formatPortPhoneNumbers(request))
    case 'portType':
      return textSortValue(getPortRequestTypeLabel(request))
    case 'phone_carrier':
      return textSortValue(request.phone_carrier)
    case 'date_time_created_at': {
      const time = Date.parse(request.date_time_created_at ?? '')
      return Number.isNaN(time) ? null : time
    }
    case 'status':
      return textSortValue(request.status)
  }
}

const getExtensionSortValue = (
  row: ClinicInternalExtensionRow,
  column: ExtensionSortColumn,
): string | number | null => {
  switch (column) {
    case 'name':
      return textSortValue(row.name)
    case 'phone_number':
      return textSortValue(normalizePhoneNumber(row.phone_number ?? ''))
    case 'team':
      return textSortValue(row.team)
    case 'extension_number': {
      const trimmed = row.extension_number.trim()
      if (!trimmed) return null
      const numeric = Number(trimmed)
      return Number.isFinite(numeric) ? numeric : trimmed.toLowerCase()
    }
  }
}

const useSortedPage = <T extends string>(): {
  currentPage: number
  pageSize: number
  sortColumnId: T | null
  sortDirection: DataTableSortDirection
  handleSort: (columnId: string) => void
  setCurrentPage: (page: number) => void
  onPageSizeChange: (size: number) => void
} => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortColumnId, setSortColumnId] = useState<T | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  const handleSort = (columnId: string): void => {
    const column = columnId as T
    if (sortColumnId !== column) {
      setSortColumnId(column)
      setSortDirection('asc')
    } else if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else {
      setSortColumnId(null)
    }
    setCurrentPage(1)
  }

  const onPageSizeChange = (size: number): void => {
    setPageSize(size)
    setCurrentPage(1)
  }

  return {
    currentPage,
    pageSize,
    sortColumnId,
    sortDirection,
    handleSort,
    setCurrentPage,
    onPageSizeChange,
  }
}

const portStatusBadgeColor = (status: string): BadgeColor => {
  const key = status.trim().toLowerCase()
  if (key === 'completed' || key === 'approved' || key === 'verified') {
    return 'vivid-green'
  }
  if (key === 'cancelled' || key === 'declined') return 'red'
  if (key === 'pending') return 'yellow'
  if (key === 'in progress' || key === 'in-progress') return 'blue'
  return 'gray'
}

const PhoneManagementContent = (): React.ReactElement | null => {
  const { teams } = useTeamCatalog()
  const hooks = usePhoneManagementCtx()
  const resubmitRequest = hooks.resubmitPortRequestData
  const isFaxResubmit =
    resubmitRequest !== null && isFaxPortRequest(resubmitRequest)
  const isPhoneResubmit =
    resubmitRequest !== null && !isFaxPortRequest(resubmitRequest)
  const [releaseConfirmLine, setReleaseConfirmLine] =
    useState<ClinicNumberRow | null>(null)
  const [cancelPortConfirm, setCancelPortConfirm] =
    useState<ITwilioPortRequest | null>(null)
  const [deleteExtensionConfirm, setDeleteExtensionConfirm] =
    useState<ClinicInternalExtensionRow | null>(null)
  const isTabSkeleton = useSettingsTabSkeleton(hooks.tab)
  const clinicNumbersById = new Map(
    hooks.filteredClinicNumbers.map((row) => [row.id, row]),
  )
  const {
    currentPage: phonePage,
    pageSize: phonePageSize,
    sortColumnId: phoneSortColumnId,
    sortDirection: phoneSortDirection,
    handleSort: handlePhoneSort,
    setCurrentPage: setPhonePage,
    onPageSizeChange: onPhonePageSizeChange,
  } = useSortedPage<PhoneSortColumn>()
  const {
    currentPage: portPage,
    pageSize: portPageSize,
    sortColumnId: portSortColumnId,
    sortDirection: portSortDirection,
    handleSort: handlePortSort,
    setCurrentPage: setPortPage,
    onPageSizeChange: onPortPageSizeChange,
  } = useSortedPage<PortSortColumn>()
  const {
    currentPage: extensionPage,
    pageSize: extensionPageSize,
    sortColumnId: extensionSortColumnId,
    sortDirection: extensionSortDirection,
    handleSort: handleExtensionSort,
    setCurrentPage: setExtensionPage,
    onPageSizeChange: onExtensionPageSizeChange,
  } = useSortedPage<ExtensionSortColumn>()
  const portRequestEntries = useMemo(
    () =>
      hooks.filteredPortRequests.map((request, index) => ({
        id: request.twilio_port_request_id || `port-${index}`,
        request,
      })),
    [hooks.filteredPortRequests],
  )
  const portRequestsById = new Map(
    portRequestEntries.map((entry) => [entry.id, entry.request]),
  )
  const extensionsById = new Map(
    hooks.filteredInternalExtensions.map((row) => [row.id, row]),
  )
  const renderClinicNumber =
    (render: (row: ClinicNumberRow) => ReactNode) =>
    (row: PhoneTableRow): ReactNode => {
      const clinicNumber = clinicNumbersById.get(row.id)
      return clinicNumber ? render(clinicNumber) : null
    }
  const renderPortRequest =
    (render: (request: ITwilioPortRequest) => ReactNode) =>
    (row: PhoneTableRow): ReactNode => {
      const request = portRequestsById.get(row.id)
      return request ? render(request) : null
    }
  const renderExtension =
    (render: (row: ClinicInternalExtensionRow) => ReactNode) =>
    (row: PhoneTableRow): ReactNode => {
      const extension = extensionsById.get(row.id)
      return extension ? render(extension) : null
    }

  useEffect(() => {
    if (hooks.mode === 'list') {
      hooks.setHeaderButtons(null)
      hooks.setHeaderTitle('Phone management')
    }
    return () => {
      hooks.setHeaderTitle(null)
      hooks.setHeaderButtons(null)
    }
  }, [hooks.mode])

  useEffect(() => {
    setPhonePage(1)
    setPortPage(1)
  }, [hooks.mainSearchQuery, hooks.mainFilters, setPhonePage, setPortPage])

  useEffect(() => {
    setExtensionPage(1)
  }, [hooks.extensionSearchQuery, hooks.extensionFilters, setExtensionPage])

  const phoneNumbersColumns: DataTableColumn<PhoneTableRow>[] = [
    {
      label: 'Line name',
      id: 'line_name',
      sortable: true,
      renderCell: renderClinicNumber((data) => {
        return (
          <div className="scp-pill-container">
            <span className="scp-pm-line-name">{data.line_name || 'N/A'}</span>
            {data.is_main && (
              <Badge mode="tinted" color="vivid-green" label="Main" />
            )}
          </div>
        )
      }),
      width: '30%',
    },
    {
      label: 'Phone number',
      id: 'phone_number',
      sortable: true,
      width: '30%',
      renderCell: renderClinicNumber((data) => data.phone_number),
    },
    {
      label: 'Supported features',
      id: 'contact_methods',
      renderCell: renderClinicNumber((data) => {
        const methods = safeParseMethods(data.contact_methods).filter(
          (m) => m !== 'fax',
        )
        return (
          <div className="scp-pill-container">
            {methods.length === 0
              ? '—'
              : methods.map((m) => (
                  <React.Fragment key={m}>{methodPill(m)}</React.Fragment>
                ))}
          </div>
        )
      }),
      width: '30%',
    },
  ]

  const portRequestsColumns: DataTableColumn<PhoneTableRow>[] = [
    {
      label: 'Phone number',
      id: 'phoneNumbers',
      width: '22%',
      sortable: true,
      renderCell: renderPortRequest((data) => formatPortPhoneNumbers(data)),
    },
    {
      label: 'Type',
      id: 'portType',
      width: '10%',
      sortable: true,
      renderCell: renderPortRequest((data) => (
        <Badge
          mode="tinted"
          color={isFaxPortRequest(data) ? 'purple' : 'blue'}
          label={getPortRequestTypeLabel(data)}
        />
      )),
    },
    {
      label: 'Carrier',
      id: 'phone_carrier',
      width: '22%',
      sortable: true,
      renderCell: renderPortRequest((data) => data.phone_carrier),
    },
    {
      label: 'Date submitted',
      id: 'date_time_created_at',
      width: '22%',
      sortable: true,
      renderCell: renderPortRequest((data) =>
        formatDate(data.date_time_created_at ?? new Date().toISOString()),
      ),
    },
    {
      label: 'Status',
      id: 'status',
      width: '10%',
      sortable: true,
      renderCell: renderPortRequest((data) => (
        <Badge
          mode="tinted"
          color={portStatusBadgeColor(data.status)}
          label={data.status}
        />
      )),
    },
  ]

  const renderPortRequestActions = renderPortRequest((data) => {
    const statusKey = (data.review_status || data.status || '')
      .trim()
      .toLowerCase()
    const workflowCompleted = ['cancelled', 'completed', 'approved'].includes(
      statusKey,
    )
    const isDeclined = statusKey === 'declined'
    const canResubmit = isDeclined && data.is_latest !== false
    const actionsDisabled = hooks.loading || workflowCompleted
    return (
      <ActionButtons
        actions={[
          {
            id: 'resubmit',
            icon: (
              <EditIcon className="ds-action-button__svg" aria-hidden="true" />
            ),
            label: 'Edit and resubmit request',
            tone: 'primary',
            variant: 'outline',
            disabled: hooks.loading || !canResubmit,
            onClick: () => {
              if (hooks.loading || !canResubmit) return
              hooks.openResubmitPortModal(data)
            },
          },
          {
            id: 'refresh',
            icon: (
              <RefreshIcon
                className="ds-action-button__svg"
                aria-hidden="true"
              />
            ),
            label: 'Refresh status',
            tone: 'primary',
            variant: 'outline',
            disabled: actionsDisabled || isDeclined,
            onClick: () => {
              if (actionsDisabled) return
              void hooks.refreshPortRequestStatus(data)
            },
          },
          {
            id: 'cancel',
            icon: (
              <CloseIcon className="ds-action-button__svg" aria-hidden="true" />
            ),
            label: 'Cancel request',
            tone: 'danger',
            variant: 'outline',
            disabled: actionsDisabled,
            onClick: () => {
              if (actionsDisabled) return
              setCancelPortConfirm(data)
            },
          },
        ]}
      />
    )
  })

  const internalExtensionsColumns: DataTableColumn<PhoneTableRow>[] = [
    {
      label: 'Name',
      id: 'name',
      sortable: true,
      width: '25%',
      renderCell: renderExtension((data) => data.name),
    },
    {
      label: 'Phone number',
      id: 'phone_number',
      sortable: true,
      width: '26%',
      renderCell: renderExtension((data) => data.phone_number || 'N/A'),
    },
    {
      label: 'Team',
      id: 'team',
      sortable: true,
      width: '26%',
      renderCell: renderExtension((data) => data.team || ''),
    },
    {
      label: 'Extension number',
      id: 'extension_number',
      sortable: true,
      renderCell: renderExtension((data) => (
        <Badge mode="tinted" color="gray" label={data.extension_number} />
      )),
      width: '14%',
    },
  ]

  const sortedClinicNumbers = useMemo(
    () =>
      sortRows(
        hooks.filteredClinicNumbers,
        phoneSortColumnId !== null,
        phoneSortDirection,
        (row) =>
          phoneSortColumnId ? getPhoneSortValue(row, phoneSortColumnId) : null,
      ),
    [hooks.filteredClinicNumbers, phoneSortColumnId, phoneSortDirection],
  )
  const phoneTotalPages = Math.max(
    1,
    Math.ceil(sortedClinicNumbers.length / phonePageSize),
  )
  const phonePageNumber = Math.min(phonePage, phoneTotalPages)
  const phonePageRows = useMemo(
    (): PhoneTableRow[] =>
      sortedClinicNumbers
        .slice(
          (phonePageNumber - 1) * phonePageSize,
          phonePageNumber * phonePageSize,
        )
        .map((row) => ({ id: row.id })),
    [sortedClinicNumbers, phonePageNumber, phonePageSize],
  )
  const sortedPortRequests = useMemo(
    () =>
      sortRows(
        portRequestEntries,
        portSortColumnId !== null,
        portSortDirection,
        (entry) =>
          portSortColumnId
            ? getPortSortValue(entry.request, portSortColumnId)
            : null,
      ),
    [portRequestEntries, portSortColumnId, portSortDirection],
  )
  const portTotalPages = Math.max(
    1,
    Math.ceil(sortedPortRequests.length / portPageSize),
  )
  const portPageNumber = Math.min(portPage, portTotalPages)
  const portPageRows = useMemo(
    (): PhoneTableRow[] =>
      sortedPortRequests
        .slice(
          (portPageNumber - 1) * portPageSize,
          portPageNumber * portPageSize,
        )
        .map((entry) => ({ id: entry.id })),
    [sortedPortRequests, portPageNumber, portPageSize],
  )
  const sortedExtensions = useMemo(
    () =>
      sortRows(
        hooks.filteredInternalExtensions,
        extensionSortColumnId !== null,
        extensionSortDirection,
        (row) =>
          extensionSortColumnId
            ? getExtensionSortValue(row, extensionSortColumnId)
            : null,
      ),
    [
      hooks.filteredInternalExtensions,
      extensionSortColumnId,
      extensionSortDirection,
    ],
  )
  const extensionTotalPages = Math.max(
    1,
    Math.ceil(sortedExtensions.length / extensionPageSize),
  )
  const extensionPageNumber = Math.min(extensionPage, extensionTotalPages)
  const extensionPageRows = useMemo(
    (): PhoneTableRow[] =>
      sortedExtensions
        .slice(
          (extensionPageNumber - 1) * extensionPageSize,
          extensionPageNumber * extensionPageSize,
        )
        .map((row) => ({ id: row.id })),
    [sortedExtensions, extensionPageNumber, extensionPageSize],
  )

  const phonesEmpty = hooks.clinicNumbers.length === 0
  const portsEmpty = hooks.portRequests.length === 0
  const extensionsEmpty = hooks.internalExtensions.length === 0
  const outlineEmptyTables =
    hooks.tab === 'main' && hooks.dataReady && phonesEmpty && extensionsEmpty

  if (!hooks.dataReady) {
    return (
      <div className="scp-phone-management">
        <SettingsSkeleton variant="table" pageKey="comm-phone-management" />
      </div>
    )
  }

  return hooks.mode === 'list' ? (
    <div className="scp-phone-management">
      <div className="scp-table-group">
        <div className="scp-table-title-container">
          <Tabs
            orientation="horizontal"
            items={[
              { label: 'Phone number', id: 'main' },
              { label: 'Port number', id: 'port' },
            ]}
            value={hooks.tab}
            onChange={hooks.switchTab}
          />
          <div className="component-buttons">
            <SearchWithFilter
              placeholder="Search"
              value={hooks.mainSearchQuery}
              onChange={hooks.setMainSearchQuery}
              filters={
                hooks.tab === 'main'
                  ? { feature: ['Call', 'Fax', 'SMS'] }
                  : {
                      status: [
                        'Pending',
                        'In progress',
                        'Completed',
                        'Cancelled',
                        'Declined',
                        'Approved',
                      ],
                      carrier: [
                        'AT&T',
                        'T-Mobile',
                        'Verizon',
                        'Sprint',
                        'Other',
                      ],
                    }
              }
              filterLabels={{
                feature: 'Feature',
                status: 'Status',
                carrier: 'Carrier',
              }}
              selectedFilters={hooks.mainFilters}
              onFilterChange={hooks.setMainFilters}
            />{' '}
            <Select
              className="scp-add-number-select"
              label=""
              value={(hooks.method as string) ?? ''}
              placeholder="Purchase number"
              options={[
                { label: 'Purchase number', value: 'purchase' },
                { label: 'Port number', value: 'port' },
              ]}
              onChange={(value) => hooks.handleAddNumber(value as string)}
            />
          </div>
        </div>
        {isTabSkeleton ? (
          <SettingsSkeleton variant="table" showToolbar={false} />
        ) : hooks.tab === 'main' ? (
          !hooks.dataReady ? null : hooks.filteredClinicNumbers.length === 0 ? (
            <EmptyState
              className="scp-pm-empty"
              illustration={<PhoneIcon className="empty-icon" />}
              title="No phone numbers found"
              description={
                phonesEmpty
                  ? "You don't have any phone numbers on record yet."
                  : 'No phone numbers match your search.'
              }
              outlined={outlineEmptyTables}
              actionLabel={phonesEmpty ? 'Add number' : undefined}
              onAction={
                phonesEmpty
                  ? () => hooks.handleAddNumber('purchase')
                  : undefined
              }
            />
          ) : (
            <DataTable<PhoneTableRow>
              className="scp-clinic-number__table"
              rows={phonePageRows}
              columns={phoneNumbersColumns}
              actionsLabel="Actions"
              renderActions={renderClinicNumber((data) => {
                const ported = hooks.isPortedClinicNumber(data)
                const canRelease = hooks.canReleasePurchasedClinicNumber(data)
                return (
                  <ActionButtons
                    onEdit={() => hooks.beginEditPhoneLine(data)}
                    middle={
                      <ActionButton
                        icon={
                          <DeleteFilledIcon
                            className="ds-action-button__svg"
                            aria-hidden="true"
                          />
                        }
                        label={
                          ported
                            ? 'Please contact EnamelAI to port out this number.'
                            : 'Delete'
                        }
                        tone="danger"
                        variant="outline"
                        disabled={ported || !canRelease || hooks.loading}
                        onClick={() => {
                          if (!canRelease || ported) return
                          setReleaseConfirmLine(data)
                        }}
                      />
                    }
                  />
                )
              })}
              sortColumnId={phoneSortColumnId ?? undefined}
              sortDirection={phoneSortColumnId ? phoneSortDirection : undefined}
              onSort={handlePhoneSort}
              pagination={{
                currentPage: phonePageNumber,
                totalPages: phoneTotalPages,
                pageSize: phonePageSize,
                onPageChange: setPhonePage,
                onPageSizeChange: onPhonePageSizeChange,
              }}
            />
          )
        ) : !hooks.dataReady ? null : hooks.filteredPortRequests.length ===
          0 ? (
          <EmptyState
            className="scp-pm-empty"
            illustration={<PortIcon className="empty-icon" />}
            title="No port requests found"
            description={
              portsEmpty
                ? "You don't have any port requests on record yet."
                : 'No port requests match your search.'
            }
            actionLabel={portsEmpty ? 'Port number' : undefined}
            onAction={
              portsEmpty ? () => hooks.handleAddNumber('port') : undefined
            }
          />
        ) : (
          <DataTable<PhoneTableRow>
            className="scp-port-requests__table"
            rows={portPageRows}
            columns={portRequestsColumns}
            actionsLabel="Actions"
            renderActions={renderPortRequestActions}
            sortColumnId={portSortColumnId ?? undefined}
            sortDirection={portSortColumnId ? portSortDirection : undefined}
            onSort={handlePortSort}
            pagination={{
              currentPage: portPageNumber,
              totalPages: portTotalPages,
              pageSize: portPageSize,
              onPageChange: setPortPage,
              onPageSizeChange: onPortPageSizeChange,
            }}
          />
        )}
      </div>

      {hooks.tab === 'main' && !isTabSkeleton && (
        <div className="scp-table-group">
          {hooks.dataReady &&
          hooks.filteredInternalExtensions.length === 0 &&
          extensionsEmpty ? null : (
            <div className="scp-table-title-container">
              <span className="scp-table-title">
                Internal extension
                <button
                  type="button"
                  className="internal-extension__info-btn ds-section-card__icon-button"
                  aria-label="Internal extension information"
                  data-bs-toggle="tooltip"
                  title="Internal extensions for staff and rooms."
                >
                  <InfoIcon aria-hidden="true" />
                </button>
              </span>
              <div className="component-buttons">
                <SearchWithFilter
                  placeholder="Search"
                  value={hooks.extensionSearchQuery}
                  onChange={hooks.setExtensionSearchQuery}
                  filters={{ team: teams }}
                  filterLabels={{ team: 'Team' }}
                  selectedFilters={hooks.extensionFilters}
                  onFilterChange={hooks.setExtensionFilters}
                />
                {!extensionsEmpty ? (
                  <Button
                    size="sm"
                    variant="primary"
                    className="scp-add-extension-button"
                    onClick={hooks.startAddExtension}
                  >
                    Add extension
                  </Button>
                ) : null}
              </div>
            </div>
          )}
          {!hooks.dataReady ? null : hooks.filteredInternalExtensions.length ===
            0 ? (
            <EmptyState
              className="scp-pm-empty"
              illustration={<PhoneIcon className="empty-icon" />}
              title="No internal extensions found"
              description={
                extensionsEmpty
                  ? "You don't have any internal extensions on record yet."
                  : 'No internal extensions match your search.'
              }
              outlined={outlineEmptyTables}
              actionLabel={extensionsEmpty ? 'Add extension' : undefined}
              onAction={extensionsEmpty ? hooks.startAddExtension : undefined}
            />
          ) : (
            <DataTable<PhoneTableRow>
              className="scp-internal-extensions__table"
              rows={extensionPageRows}
              columns={internalExtensionsColumns}
              actionsLabel="Actions"
              renderActions={renderExtension((data) => {
                return (
                  <ActionButtons
                    onEdit={() => hooks.startEditExtension(data)}
                    middle={
                      <ActionButton
                        icon={
                          <DeleteFilledIcon
                            className="ds-action-button__svg"
                            aria-hidden="true"
                          />
                        }
                        label="Delete"
                        tone="danger"
                        variant="outline"
                        disabled={hooks.loading}
                        onClick={() => setDeleteExtensionConfirm(data)}
                      />
                    }
                  />
                )
              })}
              sortColumnId={extensionSortColumnId ?? undefined}
              sortDirection={
                extensionSortColumnId ? extensionSortDirection : undefined
              }
              onSort={handleExtensionSort}
              pagination={{
                currentPage: extensionPageNumber,
                totalPages: extensionTotalPages,
                pageSize: extensionPageSize,
                onPageChange: setExtensionPage,
                onPageSizeChange: onExtensionPageSizeChange,
              }}
            />
          )}
        </div>
      )}
      <PhoneManagementDeleteNumberModal
        show={releaseConfirmLine !== null}
        phoneNumber={
          releaseConfirmLine
            ? formatPhoneNumber(releaseConfirmLine.phone_number)
            : ''
        }
        deleteDisabled={hooks.loading}
        onCancel={() => setReleaseConfirmLine(null)}
        onDelete={() => {
          if (!releaseConfirmLine) return
          void hooks.releaseClinicNumber(releaseConfirmLine)
        }}
      />

      <DeleteConfirmationModal
        show={cancelPortConfirm !== null}
        onHide={() => setCancelPortConfirm(null)}
        confirmDisabled={hooks.loading}
        bodyText="This port-in request will be canceled. Communications through this number will no longer be managed by EnamelAI.\n\nDo you want to continue?"
        title="Cancel port request"
        onConfirm={() => {
          if (!cancelPortConfirm) return
          void hooks.cancelPortRequest(cancelPortConfirm)
        }}
      />

      <PhoneManagementDeleteExtensionModal
        show={deleteExtensionConfirm !== null}
        extensionNumber={deleteExtensionConfirm?.extension_number ?? ''}
        extensionName={deleteExtensionConfirm?.name}
        deleteDisabled={hooks.loading}
        onCancel={() => setDeleteExtensionConfirm(null)}
        onDelete={() => {
          const id = deleteExtensionConfirm?.clinic_internal_extension_id
          if (!id) return
          void hooks.deleteInternalExtension(id)
        }}
      />

      <PortNumberModal
        key="port-new"
        show={hooks.method === 'port'}
        onHide={hooks.onHidePortNumberModal}
      />
      <PortNumberModal
        key="port-resubmit"
        show={isPhoneResubmit}
        onHide={hooks.closeResubmitPortModal}
        resubmitRequest={resubmitRequest}
      />
      <PortFaxModal
        key="fax-resubmit"
        show={isFaxResubmit}
        clinicId={hooks.clinicId}
        onHide={hooks.closeResubmitPortModal}
        onAdded={() => undefined}
        resubmitRequest={resubmitRequest}
        runAsync={hooks.runAsync}
        loadingContainer=".scp-phone-management-container"
      />
      <PurchaseNumberModal
        show={hooks.method === 'purchase'}
        onHide={hooks.onHidePortNumberModal}
      />
    </div>
  ) : hooks.mode === 'addNumber' ? (
    <AddPhoneNumber />
  ) : hooks.mode === 'editPhoneNumber' ? (
    <EditPhoneNumber />
  ) : hooks.mode === 'addExtension' ? (
    <AddExtension />
  ) : null
}

const PhoneManagement = (): React.ReactElement => {
  return (
    <PhoneManagementProvider>
      <PhoneManagementContent />
    </PhoneManagementProvider>
  )
}

export default withTeamCatalog(PhoneManagement)
